/*
 * Archive.jsx
 * -----------
 * Shows all completed checklist items across all checklists.
 * Grouped by checklist, searchable, and each item can be restored.
 * Authenticated users only -- route guarded in App.jsx.
 * Fetches all checklists and filters for checked items client side.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import CategoryTag from "../components/CategoryTag";

// ============================================
// SEARCH BAR
// Simple local search, no API call needed
// ============================================
function ArchiveSearch({ query, onChange }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "var(--space-3)",
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-md)",
      padding: "var(--space-3) var(--space-4)"
    }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: "var(--text-muted)", flexShrink: 0 }}>
        <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <input
        type="text"
        value={query}
        onChange={e => onChange(e.target.value)}
        placeholder="Search completed items..."
        style={{
          flex: 1,
          background: "none",
          border: "none",
          outline: "none",
          fontFamily: "var(--font-body)",
          fontSize: "15px",
          color: "var(--text-primary)",
          caretColor: "var(--accent-green)"
        }}
      />
      {query && (
        <button
          onClick={() => onChange("")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--text-muted)",
            padding: "2px",
            display: "flex",
            alignItems: "center"
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      )}
    </div>
  );
}

// ============================================
// ARCHIVED ITEM ROW
// ============================================
function ArchivedItemRow({ item, onRestore }) {
  const [restoring, setRestoring] = useState(false);

  async function handleRestore() {
    setRestoring(true);
    try {
      await onRestore(item);
    } finally {
      setRestoring(false);
    }
  }

  function formatDate(isoString) {
    if (!isoString) return null;
    return new Date(isoString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  }

  const priorityColors = {
    high: "var(--priority-high)",
    medium: "var(--priority-medium)",
    low: "var(--priority-low)"
  };

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "var(--space-3)",
      padding: "var(--space-3) var(--space-4)",
      borderBottom: "1px solid var(--border)",
      borderLeft: "3px solid " + (priorityColors[item.priority] || "var(--border)"),
      transition: "background var(--transition-fast)"
    }}
    onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >

      {/* Checked indicator */}
      <div style={{
        width: "20px",
        height: "20px",
        borderRadius: "6px",
        background: "var(--accent-green-muted)",
        border: "2px solid var(--accent-green)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      }}>
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="var(--accent-green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Item content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "14px",
          fontWeight: 500,
          color: "var(--text-muted)",
          textDecoration: "line-through",
          margin: "0 0 2px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          {item.text}
        </p>
        {item.dueDate && (
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "11px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            Was due {formatDate(item.dueDate)}
          </p>
        )}
        {item.notes && (
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            color: "var(--text-muted)",
            margin: "2px 0 0",
            fontStyle: "italic",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
            {item.notes}
          </p>
        )}
      </div>

      {/* Restore button */}
      <button
        onClick={handleRestore}
        disabled={restoring}
        style={{
          background: "none",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          padding: "var(--space-1) var(--space-3)",
          cursor: restoring ? "not-allowed" : "pointer",
          fontFamily: "var(--font-body)",
          fontSize: "12px",
          fontWeight: 500,
          color: "var(--accent-green)",
          whiteSpace: "nowrap",
          transition: "background var(--transition-fast), border-color var(--transition-fast)",
          opacity: restoring ? 0.6 : 1,
          flexShrink: 0
        }}
        onMouseEnter={e => {
          if (!restoring) {
            e.currentTarget.style.background = "var(--accent-green-muted)";
            e.currentTarget.style.borderColor = "var(--accent-green)";
          }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = "none";
          e.currentTarget.style.borderColor = "var(--border)";
        }}
      >
        {restoring ? "Restoring..." : "↩ Restore"}
      </button>
    </div>
  );
}

// ============================================
// CHECKLIST GROUP
// Groups archived items under their checklist
// ============================================
function ChecklistGroup({ checklist, items, query, onRestore }) {
  const [collapsed, setCollapsed] = useState(false);

  const filtered = query
    ? items.filter(item => item.text.toLowerCase().includes(query.toLowerCase()))
    : items;

  if (filtered.length === 0) return null;

  return (
    <div style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-md)",
      overflow: "hidden"
    }}>

      {/* Group header */}
      <button
        onClick={() => setCollapsed(prev => !prev)}
        style={{
          width: "100%",
          background: "var(--bg-surface-2)",
          border: "none",
          borderBottom: collapsed ? "none" : "1px solid var(--border)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "var(--space-3) var(--space-4)",
          gap: "var(--space-3)"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <h3 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "16px",
            color: "var(--text-primary)",
            margin: 0
          }}>
            {checklist.title}
          </h3>
          <CategoryTag category={checklist.category} size="sm" showIcon={false} />
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            color: "var(--text-muted)"
          }}>
            {filtered.length} completed
          </span>
        </div>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{
            color: "var(--text-muted)",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform var(--transition-base)",
            flexShrink: 0
          }}
        >
          <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Items */}
      {!collapsed && filtered.map(item => (
        <ArchivedItemRow
          key={item.id}
          item={item}
          onRestore={onRestore}
        />
      ))}
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function Archive() {
  const { user } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  // ============================================
  // FETCH CHECKLISTS
  // ============================================
  const fetchChecklists = useCallback(async () => {
    try {
      const res = await fetch("/api/checklists", {
        headers: { "Content-Type": "application/json" }
      });
      const data = await res.json();
      setChecklists(data);
    } catch (err) {
      console.error("Failed to fetch checklists", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  // ============================================
  // RESTORE ITEM
  // Unchecks the item and saves back to API
  // ============================================
  async function handleRestore(item) {
    const checklist = checklists.find(c => c.id === item.checklistId);
    if (!checklist) return;

    const updatedItems = checklist.items.map(i =>
      i.id === item.id ? { ...i, checked: false } : i
    );

    // Optimistic update
    setChecklists(prev => prev.map(c =>
      c.id === checklist.id ? { ...c, items: updatedItems } : c
    ));

    try {
      await fetch("/api/checklists", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: checklist.id,
          title: checklist.title,
          items: updatedItems,
          category: checklist.category,
          recurring: checklist.recurring,
          recurringInterval: checklist.recurringInterval
        })
      });
    } catch (err) {
      console.error("Failed to restore item", err);
      fetchChecklists();
    }
  }

  // ============================================
  // DERIVED DATA
  // Only checklists that have completed items
  // ============================================
  const checklistsWithArchive = checklists
    .map(c => ({
      ...c,
      archivedItems: (c.items || [])
        .filter(i => i.checked)
        .map(i => ({ ...i, checklistId: c.id }))
    }))
    .filter(c => c.archivedItems.length > 0);

  const totalArchived = checklistsWithArchive.reduce(
    (sum, c) => sum + c.archivedItems.length, 0
  );

  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh"
      }}>
        <p style={{ fontFamily: "var(--font-body)", color: "var(--text-muted)" }}>
          Loading archive...
        </p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: "800px",
      margin: "0 auto",
      padding: "var(--space-6) var(--space-4)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-5)"
    }}>

      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "var(--space-3)"
      }}>
        <div>
          <h1 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 900,
            fontSize: "clamp(28px, 5vw, 40px)",
            color: "var(--text-primary)",
            margin: "0 0 var(--space-2)",
            letterSpacing: "-1px"
          }}>
            Archive
          </h1>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "14px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            {totalArchived} completed item{totalArchived !== 1 ? "s" : ""} across {checklistsWithArchive.length} checklist{checklistsWithArchive.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Search */}
      <ArchiveSearch query={query} onChange={setQuery} />

      {/* Checklist groups */}
      {checklistsWithArchive.length === 0 ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--space-8)",
          gap: "var(--space-4)",
          textAlign: "center"
        }}>
          <span style={{ fontSize: "64px" }}>📭</span>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "24px",
            color: "var(--text-primary)",
            margin: 0
          }}>
            Nothing archived yet
          </h2>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "15px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            Completed items will appear here once you start checking things off.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          {checklistsWithArchive.map(checklist => (
            <ChecklistGroup
              key={checklist.id}
              checklist={checklist}
              items={checklist.archivedItems}
              query={query}
              onRestore={handleRestore}
            />
          ))}
        </div>
      )}
    </div>
  );
}